import React, { useEffect, useState } from 'react';
import { Copy, Check, FolderOpen, Send, Upload, Users, FileText, Trash2, Search, Eye, Key, Briefcase, LogOut, Sparkles, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import CaseStudyBuilder from './CaseStudyBuilder';
import LinkedInAutomation from './LinkedInAutomation';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('blogs');

  // Blog states
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [image, setImage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [attachment, setAttachment] = useState(null);

  // Client Portal states
  const [clientName, setClientName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [projectFiles, setProjectFiles] = useState([]);
  const [filePermission, setFilePermission] = useState('view'); // 'view' or 'download'
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { projectId, projectName }
  const [deleteBlogConfirm, setDeleteBlogConfirm] = useState(null); // { blogId, blogTitle }
  const [successMessage, setSuccessMessage] = useState(null); // Success confirmation message

  // Portfolio Projects states
  const [portfolioTitle, setPortfolioTitle] = useState('');
  const [portfolioDescription, setPortfolioDescription] = useState('');
  const [portfolioHtmlFile, setPortfolioHtmlFile] = useState(null);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [viewingPortfolioProject, setViewingPortfolioProject] = useState(null);
  const [deletePortfolioConfirm, setDeletePortfolioConfirm] = useState(null); // { projectId, projectTitle }

  const api = process.env.REACT_APP_API_BASE || '';
  const token = localStorage.getItem('token');

  const loadPosts = async () => {
    try {
      const res = await fetch(api + '/api/blogs');
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await fetch(api + '/api/projects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('token');
        navigate('/admin/login');
        return;
      }
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadPortfolioProjects = async () => {
    try {
      const res = await fetch(api + '/api/portfolio-projects');
      const data = await res.json();
      setPortfolioProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading portfolio projects:', error);
      setPortfolioProjects([]);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadPosts();
    loadProjects();
    loadPortfolioProjects();
  }, []);

  const submitBlog = async (e) => {
    e.preventDefault();
    let imageUrl = '';
    let attachmentUrl = '';
    let attachmentName = '';
    if (image) {
      const fd = new FormData();
      fd.append('image', image);
      const up = await fetch(api + '/api/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (up.ok) imageUrl = (await up.json()).url;
    }
    if (attachment) {
      const fd2 = new FormData();
      fd2.append('image', attachment);
      const up2 = await fetch(api + '/api/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd2
      });
      if (up2.ok) {
        const res2 = await up2.json();
        attachmentUrl = res2.url;
        attachmentName = attachment.name || '';
      }
    }

    const res = await fetch(api + '/api/blogs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        title, excerpt, content, imageUrl, attachmentUrl, attachmentName, tags: tags.split(',').map((t) => t.trim()).filter(Boolean)
      })
    });
    if (res.ok) {
      setTitle(''); setExcerpt(''); setContent(''); setTags(''); setImage(null); setAttachment(null);
      await loadPosts();
      alert('Post created');
    } else {
      alert('Failed to create');
    }
  };

  const submitProject = async (e) => {
    e.preventDefault();

    if (!clientName || !projectName || !clientEmail || projectFiles.length === 0) {
      alert('Please fill all fields and upload at least one file');
      return;
    }

    setUploading(true);

    try {
      const fd = new FormData();
      fd.append('clientName', clientName);
      fd.append('projectName', projectName);
      fd.append('clientEmail', clientEmail);
      fd.append('emailSubject', emailSubject);
      fd.append('filePermission', filePermission);

      for (let file of projectFiles) {
        fd.append('files', file);
      }

      const res = await fetch(api + '/api/projects', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });

      if (res.ok) {
        const project = await res.json();
        setClientName('');
        setProjectName('');
        setClientEmail('');
        setEmailSubject('');
        setProjectFiles([]);
        setFilePermission('view');
        document.getElementById('project-files').value = '';
        await loadProjects();
        alert(`Project created! Email sent to ${clientEmail}\n\nAccess Key: ${project.accessKey}\nURL: ${project.clientUrl}`);
      } else {
        const error = await res.json();
        alert('Failed to create project: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error creating project: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const deleteProject = async (projectId) => {
    try {
      const res = await fetch(`${api}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.status === 401) {
        alert('Your session has expired. Please log in again.');
        logout();
        return;
      }

      if (res.ok) {
        await loadProjects();
        setDeleteConfirm(null);
        setSuccessMessage('Project deleted successfully');
      } else {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          alert('Failed to delete project: ' + (error.error || 'Unknown error'));
        } else {
          alert('Failed to delete project: Server returned an unexpected response');
        }
      }
    } catch (error) {
      console.error('Delete project error:', error);
      alert('Error deleting project. Please check your connection and try again.');
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      const res = await fetch(`${api}/api/blogs/${blogId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.status === 401) {
        alert('Your session has expired. Please log in again.');
        logout();
        return;
      }

      if (res.ok) {
        await loadPosts();
        setDeleteBlogConfirm(null);
        setSuccessMessage('Blog deleted successfully');
      } else {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          alert('Failed to delete blog: ' + (error.error || 'Unknown error'));
        } else {
          alert('Failed to delete blog: Server returned an unexpected response');
        }
      }
    } catch (error) {
      console.error('Delete blog error:', error);
      alert('Error deleting blog. Please check your connection and try again.');
    }
  };

  const submitPortfolioProject = async (e) => {
    e.preventDefault();

    if (!portfolioHtmlFile) {
      alert('Please select an HTML file');
      return;
    }

    const formData = new FormData();
    formData.append('title', portfolioTitle);
    formData.append('description', portfolioDescription);
    formData.append('htmlFile', portfolioHtmlFile);

    try {
      const res = await fetch(`${api}/api/portfolio-projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (res.ok) {
        await loadPortfolioProjects();
        setPortfolioTitle('');
        setPortfolioDescription('');
        setPortfolioHtmlFile(null);
        setSuccessMessage('Portfolio project created successfully');
      } else {
        const error = await res.json();
        alert('Failed to create portfolio project: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Create portfolio project error:', error);
      alert('Error creating portfolio project. Please try again.');
    }
  };

  const deletePortfolioProject = async (projectId) => {
    try {
      const res = await fetch(`${api}/api/portfolio-projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.status === 401) {
        alert('Your session has expired. Please log in again.');
        logout();
        return;
      }

      if (res.ok) {
        await loadPortfolioProjects();
        setDeletePortfolioConfirm(null);
        setSuccessMessage('Portfolio project deleted successfully');
      } else {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await res.json();
          alert('Failed to delete portfolio project: ' + (error.error || 'Unknown error'));
        } else {
          alert('Failed to delete portfolio project: Server returned an unexpected response');
        }
      }
    } catch (error) {
      console.error('Delete portfolio project error:', error);
      alert('Error deleting portfolio project. Please check your connection and try again.');
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter((p) =>
    p.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.clientEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-extrabold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Admin Dashboard</h1>
            <div className="text-gray-500">Manage your content and client projects</div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/change-password')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all hover:scale-105"
            >
              <Key className="w-4 h-4" />
              Change Password
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-lg transition-all hover:scale-105"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('blogs')}
              className={`pb-4 px-2 font-semibold transition-all ${
                activeTab === 'blogs'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Blog Posts
              </div>
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              className={`pb-4 px-2 font-semibold transition-all ${
                activeTab === 'projects'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Projects
              </div>
            </button>
            <button
              onClick={() => setActiveTab('portal')}
              className={`pb-4 px-2 font-semibold transition-all ${
                activeTab === 'portal'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Client Portal
              </div>
            </button>
            <button
              onClick={() => setActiveTab('casestudies')}
              className={`pb-4 px-2 font-semibold transition-all ${
                activeTab === 'casestudies'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Case Studies
              </div>
            </button>
            <button
              onClick={() => setActiveTab('linkedin')}
              className={`pb-4 px-2 font-semibold transition-all ${
                activeTab === 'linkedin'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                LinkedIn Automation
              </div>
            </button>
          </nav>
        </div>

        {/* Blog Posts Tab */}
        {activeTab === 'blogs' && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-pink-400 to-cyan-400 rounded-2xl blur opacity-25"></div>
                <div className="relative bg-white rounded-2xl shadow-xl p-6">
                  <h2 className="text-xl font-bold mb-4">New Post</h2>
                  <form onSubmit={submitBlog} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter blog title" className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                      <input value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Short summary" className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                      <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your blog content here..." rows="6" className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                      <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="DevOps, AWS, Kubernetes" className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
                      <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Attachment (PDF/Doc)</label>
                      <input type="file" accept="application/pdf,.doc,.docx" onChange={(e) => setAttachment(e.target.files?.[0] || null)} className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                    </div>
                    <button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-transform hover:scale-[1.01]">Publish Post</button>
                  </form>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h2 className="text-xl font-bold mb-4">Posts ({posts.length})</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {posts.map((p) => (
                    <div key={p.id} className="border border-gray-100 rounded-xl p-4 hover:shadow-lg transition-shadow relative">
                      {p.imageUrl && (
                        <img
                          src={p.imageUrl.startsWith('http') ? p.imageUrl : api + p.imageUrl}
                          alt={p.title}
                          className="w-full h-48 object-cover rounded-lg mb-3"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-bold text-lg flex-1">{p.title}</div>
                          <button
                            onClick={() => setDeleteBlogConfirm({ blogId: p.id, blogTitle: p.title })}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            title="Delete Blog"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {p.excerpt && <p className="text-sm text-gray-600 mb-2">{p.excerpt}</p>}
                        <div className="text-xs text-gray-500 mb-2">
                          {p.createdAt ? new Date(p.createdAt).toLocaleString() : ''}
                        </div>
                        {p.tags && p.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {p.tags.map((tag) => (
                              <span key={tag} className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {!posts.length && (
                    <div className="col-span-2 text-center py-12">
                      <div className="text-gray-400 text-lg">No posts yet</div>
                      <div className="text-gray-500 text-sm">Create your first blog post using the form</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Projects Tab */}
        {activeTab === 'projects' && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              {/* Create Project Form */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                  Add Portfolio Project
                </h2>
                <form onSubmit={submitPortfolioProject} className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Project Title *</label>
                    <input
                      value={portfolioTitle}
                      onChange={(e) => setPortfolioTitle(e.target.value)}
                      required
                      placeholder="Enter project title"
                      className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Description *</label>
                    <textarea
                      value={portfolioDescription}
                      onChange={(e) => setPortfolioDescription(e.target.value)}
                      required
                      rows="4"
                      placeholder="Brief description of the project"
                      className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">HTML File *</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".html,.htm"
                        onChange={(e) => setPortfolioHtmlFile(e.target.files[0])}
                        required
                        className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-purple-500 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Upload an HTML file showcasing your project (max 5MB)</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    <Upload className="w-5 h-5" />
                    Create Project
                  </button>
                </form>
              </div>
            </div>

            <div className="lg:col-span-2">
              {/* Projects List */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Portfolio Projects ({portfolioProjects.length})</h2>

                {portfolioProjects.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No portfolio projects yet. Create your first one!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {portfolioProjects.map((project) => (
                      <div
                        key={project.id}
                        className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-gray-900 mb-2">{project.title}</h3>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <FileText className="w-4 h-4" />
                              {project.htmlFile}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setViewingPortfolioProject(project)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Project"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setDeletePortfolioConfirm({ projectId: project.id, projectTitle: project.title })}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Project"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Client Portal Tab */}
        {activeTab === 'portal' && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 via-indigo-400 to-purple-400 rounded-2xl blur opacity-25"></div>
                <div className="relative bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold">New Project</h2>
                  </div>
                  <form onSubmit={submitProject} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                      <input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="John Doe"
                        className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                      <input
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="Website Redesign"
                        className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client Email *</label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="client@example.com"
                        className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Your Project Files - {projectName}"
                        className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to use default subject</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Project Files * (Max 20 files)
                        </div>
                      </label>
                      <input
                        id="project-files"
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.html,.xls,.xlsx"
                        onChange={(e) => setProjectFiles([...e.target.files])}
                        className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Supported: JPEG, PNG, PDF, Word, HTML, Excel</p>
                      {projectFiles.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <p className="text-sm font-medium text-gray-700">{projectFiles.length} file(s) selected:</p>
                          {projectFiles.map((file, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {file.name} ({formatFileSize(file.size)})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* File Permission Radio Buttons */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">File Permissions *</label>
                      <div className="space-y-3">
                        <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-purple-300 transition-colors">
                          <input
                            type="radio"
                            value="view"
                            checked={filePermission === 'view'}
                            onChange={(e) => setFilePermission(e.target.value)}
                            className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900">View Only</div>
                            <div className="text-xs text-gray-600">Clients can view files in the portal but cannot download them</div>
                          </div>
                        </label>
                        <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-purple-300 transition-colors">
                          <input
                            type="radio"
                            value="download"
                            checked={filePermission === 'download'}
                            onChange={(e) => setFilePermission(e.target.value)}
                            className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900">Allow Download</div>
                            <div className="text-xs text-gray-600">Clients can both view and download files</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={uploading}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Save & Send Email
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold">Client Projects ({projects.length})</h2>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by client name, project name, or email..."
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  {searchQuery && (
                    <p className="text-sm text-gray-600 mt-2">
                      Found {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  {filteredProjects.map((project) => (
                    <div key={project.id} className="border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{project.projectName}</h3>
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Active</span>
                            {project.filePermission === 'download' ? (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Download Enabled</span>
                            ) : (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">View Only</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Client:</span> {project.clientName}
                          </p>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Email:</span> {project.clientEmail}
                          </p>
                          <p className="text-xs text-gray-500">
                            Created: {new Date(project.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => setDeleteConfirm({ projectId: project.id, projectName: project.projectName, clientName: project.clientName })}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Project"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-3 mb-3">
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="text-xs font-medium text-purple-600 mb-1">Client URL</div>
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-gray-700 flex-1 truncate">{project.clientUrl}</code>
                            <button
                              onClick={() => copyToClipboard(project.clientUrl, `url-${project.id}`)}
                              className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                              title="Copy URL"
                            >
                              {copiedId === `url-${project.id}` ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-purple-600" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="bg-indigo-50 rounded-lg p-3">
                          <div className="text-xs font-medium text-indigo-600 mb-1">Access Key</div>
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-gray-700 flex-1">{project.accessKey}</code>
                            <button
                              onClick={() => copyToClipboard(project.accessKey, `key-${project.id}`)}
                              className="p-2 hover:bg-indigo-100 rounded-lg transition-colors"
                              title="Copy Access Key"
                            >
                              {copiedId === `key-${project.id}` ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-indigo-600" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-600 mb-2">Files ({project.files.length})</div>
                        <div className="grid grid-cols-2 gap-2">
                          {project.files.slice(0, 4).map((file, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              <span className="truncate">{file.originalName}</span>
                            </div>
                          ))}
                          {project.files.length > 4 && (
                            <div className="text-xs text-gray-500 italic">
                              +{project.files.length - 4} more...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!filteredProjects.length && projects.length > 0 && (
                    <div className="text-center py-12">
                      <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <div className="text-gray-400 text-lg">No projects found</div>
                      <div className="text-gray-500 text-sm">Try adjusting your search query</div>
                    </div>
                  )}
                  {!projects.length && (
                    <div className="text-center py-12">
                      <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <div className="text-gray-400 text-lg">No projects yet</div>
                      <div className="text-gray-500 text-sm">Create your first client project using the form</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scale-in">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Delete Project</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the project <span className="font-semibold text-gray-900">"{deleteConfirm.projectName}"</span> for client <span className="font-semibold text-gray-900">{deleteConfirm.clientName}</span>?
              </p>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Warning: This will permanently delete:
                </p>
                <ul className="mt-2 text-sm text-red-700 space-y-1 ml-4">
                  <li>• All project files and documents</li>
                  <li>• Project metadata and settings</li>
                  <li>• Client access links</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteProject(deleteConfirm.projectId)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Blog Confirmation Modal */}
      {deleteBlogConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scale-in">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Delete Blog Post</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the blog post <span className="font-semibold text-gray-900">"{deleteBlogConfirm.blogTitle}"</span>?
              </p>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Warning: This will permanently delete:
                </p>
                <ul className="mt-2 text-sm text-red-700 space-y-1 ml-4">
                  <li>• The blog post content</li>
                  <li>• Associated images and attachments</li>
                  <li>• All metadata and tags</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => setDeleteBlogConfirm(null)}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteBlog(deleteBlogConfirm.blogId)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl"
              >
                Delete Blog
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Project Viewer Modal */}
      {viewingPortfolioProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{viewingPortfolioProject.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{viewingPortfolioProject.description}</p>
              </div>
              <button
                onClick={() => setViewingPortfolioProject(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Iframe Viewer */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={`${api}${viewingPortfolioProject.htmlUrl}`}
                className="w-full h-full border-0"
                title={viewingPortfolioProject.title}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Portfolio Project Confirmation Modal */}
      {deletePortfolioConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scale-in">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Delete Portfolio Project</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the portfolio project <span className="font-semibold text-gray-900">"{deletePortfolioConfirm.projectTitle}"</span>?
              </p>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Warning: This will permanently delete:
                </p>
                <ul className="mt-2 text-sm text-red-700 space-y-1 ml-4">
                  <li>• The project title and description</li>
                  <li>• The uploaded HTML file</li>
                  <li>• All project data</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => setDeletePortfolioConfirm(null)}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePortfolioProject(deletePortfolioConfirm.projectId)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Confirmation Modal */}
      {successMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scale-in">
            {/* Header with Success Icon */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-10 h-10 text-white" strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Success!</h3>
                  <p className="text-sm text-gray-600">Operation completed successfully</p>
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="p-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium text-center text-lg">
                  ✓ {successMessage}
                </p>
              </div>
            </div>

            {/* Close Button */}
            <div className="p-6 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setSuccessMessage(null)}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case Studies Tab */}
      {activeTab === 'casestudies' && (
        <CaseStudyBuilder />
      )}

      {/* LinkedIn Automation Tab */}
      {activeTab === 'linkedin' && (
        <LinkedInAutomation />
      )}
    </div>
  );
}
